import { Component, ViewChild, OnInit, OnDestroy, ElementRef, inject, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/operators';
import { DateTime as LuxonDateTime } from 'luxon';

import { WindowService } from 'src/shared/providers/window.service';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';
import { buildSubRatingsSummary } from 'src/shared/util/review-sub-ratings';
import { ratingFraction, ratingLabel, ratingVariant } from 'src/shared/util/review-rating';

import { PropertyReviewsService, GuestReview, ExpectedReview, ReviewResponseStatus } from 'src/shared/services/property-reviews.service';

import { ReviewDetailComponent } from 'src/pages/marketing/components/review-detail/review-detail.component';
import { LeaveGuestReviewComponent } from 'src/pages/marketing/components/leave-guest-review/leave-guest-review.component';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

// Which column the reviews table is sorted by. tidy-table emits the column
// label, so SORT_KEYS maps that back onto one of these.
export type ReviewSortKey = 'guest' | 'property' | 'channel' | 'rating' | 'review' | 'stay' | 'response';

// The Reviews tab has two surfaces: 'inbound' (reviews guests left us) and
// 'expected' (checked-out reservations we still owe a guest review for).
export type ReviewsView = 'inbound' | 'expected';

// Sort columns for the expected (outbound) table.
export type ExpectedSortKey = 'property' | 'guest' | 'channel' | 'stay' | 'status';

// Display-ready view of a review, shared by the desktop table and the mobile
// card list. Keeps slug->label mapping and the rating visual buckets out of
// the template.
interface ReviewCard {
  id: number;
  listingName: string;
  guestName: string;
  channel: string;
  ratingValue: number;
  // Rating as a 0–1 fraction of the platform's scale, so ratings sort correctly
  // across platforms with different maxima (Airbnb /5 vs VRBO/Booking.com /10).
  ratingFraction: number;
  ratingLabel: string;
  ratingVariant: string;
  iconSrc: string;
  comment: string;
  stayDates: string;
  subRatingsSummary: string;
  responseLabel: string;
  responseVariant: string;
  responsePillClass: string;
}

// Display-ready view of an expected (outbound) review, shared by the desktop
// table and the mobile card list.
interface ExpectedReviewCard {
  id: number;
  listingName: string;
  guestName: string;
  channel: string;
  stayDates: string;
  checkOutSort: number;
  statusLabel: string;
  statusVariant: string;
  statusPillClass: string;
}

@Component({
  selector: 'reviews-tab',
  templateUrl: './reviews-tab.component.html',
  styleUrls: ['./reviews-tab.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ReviewsTabComponent implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private propertyReviewsService = inject(PropertyReviewsService);
  private util = inject(Util);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private aboutThisPageService = inject(AboutThisPageService);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private storage = inject(TidyStorage);

  readonly windowService = inject(WindowService);

  // Raw records behind the display cards, keyed by the id shown on each row, so
  // a row/card click can hand the full record to the detail panel.
  private reviewsById = new Map<number, GuestReview>();
  private expectedById = new Map<number, ExpectedReview>();

  private static readonly PLATFORM_LABELS: Record<string, string> = {
    airbnb: 'Airbnb',
    vrbo: 'VRBO',
    booking_com: 'Booking.com',
  };

  // tidy-table emits the column label it was given, so sorting maps the label
  // back onto the ReviewCard field rather than sorting the rendered cell value
  // (Rating renders HTML, and would otherwise sort by its markup).
  private static readonly SORT_KEYS: Record<string, ReviewSortKey> = {
    Guest: 'guest',
    Property: 'property',
    Channel: 'channel',
    Rating: 'rating',
    Review: 'review',
    Stay: 'stay',
    Response: 'response',
  };

  // Response status → display label + pill palette. 'Needs Response' is an
  // action item (amber), 'Responded' is done (green), mirroring the expected
  // (outbound) view's needs-review / review-left pills. 'No Response' (grey) is
  // an old review that was never answered.
  private static readonly RESPONSE_STATUS_META: Record<ReviewResponseStatus, { label: string; variant: string; pillClass: string }> = {
    responded:      { label: 'Responded',      variant: 'impact-high',   pillClass: 'reviews-status-pill--review-left' },
    needs_response: { label: 'Needs Response', variant: 'impact-medium', pillClass: 'reviews-status-pill--needs-review' },
    no_response:    { label: 'No Response',    variant: 'impact-low',    pillClass: 'reviews-status-pill--no-response' },
    failed:         { label: 'Response Failed', variant: 'impact-failed', pillClass: 'reviews-status-pill--failed' },
  };

  // Pre-tinted star assets stand in for the icon tile's variant filters — the
  // table renders cell icons as a plain tidy-image, with no class to hook onto.
  private static readonly RATING_STAR_ICONS: Record<string, string> = {
    'impact-high': 'assets/svg/star-tidy-green.svg',
    'impact-medium': 'assets/svg/star-yellow.svg',
    'impact-low': 'assets/svg/star-grey.svg',
  };

  // Mirrors tidy-impact-pill's palette. Inline `!important` on the text color is
  // required: the table paints every non-first cell's spans with --secondary-text.
  private static readonly RATING_PILL_COLORS: Record<string, { background: string; text: string }> = {
    'impact-high': { background: 'rgba(65, 202, 183, 0.18)', text: '#41cab7' },
    'impact-medium': { background: 'rgba(224, 138, 0, 0.18)', text: '#e08a00' },
    'impact-low': { background: 'rgba(153, 153, 153, 0.18)', text: '#999999' },
    'impact-failed': { background: 'rgba(235, 87, 87, 0.18)', text: '#EB5757' },
  };

  // Maps the expected table's column labels back onto ExpectedReviewCard fields,
  // mirroring SORT_KEYS for the inbound table.
  private static readonly EXPECTED_SORT_KEYS: Record<string, ExpectedSortKey> = {
    Property: 'property',
    Guest: 'guest',
    Channel: 'channel',
    Stay: 'stay',
    Status: 'status',
  };

  readonly headers = ['Guest', 'Property', 'Channel', 'Rating', 'Review', 'Stay', 'Response', 'last-column'];
  readonly expectedHeaders = ['Property', 'Guest', 'Channel', 'Stay', 'Status', 'last-column'];

  readonly viewTabs: { value: ReviewsView; label: string; icon: string }[] = [
    { value: 'inbound', label: 'Received', icon: 'assets/svg/file-tray-outline.svg' },
    { value: 'expected', label: 'To Review', icon: 'assets/svg/create-outline.svg' },
  ];

  selectedView: ReviewsView = 'inbound';

  private allReviewCards: ReviewCard[] = [];
  reviewCards: ReviewCard[] = [];
  rows: any[] = [];
  sortKey: ReviewSortKey = 'stay';
  sortAsc = false;
  isDataLoaded = false;

  private allExpectedCards: ExpectedReviewCard[] = [];
  expectedCards: ExpectedReviewCard[] = [];
  expectedRows: any[] = [];
  expectedSortKey: ExpectedSortKey = 'stay';
  expectedSortAsc = false;
  isExpectedLoaded = false;
  private isExpectedLoading = false;

  searchForm = this.fb.group({ search: [''] });
  errorMessage: string;
  showAboutThisPage = false;
  overlayTop: string;
  private searchText = '';

  private _reviewsHeader: ElementRef;
  private overlayResizeObserver: ResizeObserver;
  @ViewChild('reviewsHeader', { read: ElementRef }) set reviewsHeader(el: ElementRef) {
    this._reviewsHeader = el;
    if (el && this.showAboutThisPage && !this.overlayResizeObserver) {
      this.setupOverlayObserver();
    }
  }

  async ngOnInit() {
    this.searchForm.get('search')!.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.searchText = (value || '').toLowerCase();
      this.refreshActiveView();
      this.cdr.markForCheck();
    });

    // A reply or a guest review saved from the detail panel (desktop) or page (mobile).
    this.propertyReviewsService.reviewsChanged$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(list => list === 'inbound' ? this.reloadInbound() : this.reloadExpected());

    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('reviews');
    if (this.showAboutThisPage) {
      this.loadMockData();
      return;
    }

    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load reviews';
      this.util.showError(this.errorMessage, 10000);
    }
  }

  async loadPage() {
    let reviews: GuestReview[] = [];
    try {
      reviews = await this.propertyReviewsService.getGuestReviews();
    } catch {
      reviews = [];
    }
    this.applyReviews(reviews);
  }

  private applyReviews(reviews: GuestReview[]): void {
    this.reviewsById = new Map(reviews.map(review => [review.id, review]));
    this.allReviewCards = reviews.map(review => this.toReviewCard(review));
    this.refreshView();
    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  private toReviewCard(review: GuestReview): ReviewCard {
    const rating = review.overall_rating || 0;
    const scale = review.rating_scale || 5;
    const statusMeta = ReviewsTabComponent.RESPONSE_STATUS_META;
    const response = statusMeta[review.response_status ?? 'needs_response'] ?? statusMeta.needs_response;
    return {
      id: review.id,
      listingName: review.listing_name || 'Unknown Property',
      guestName: review.guest_name || '',
      channel: this.formatPlatform(review.platform),
      ratingValue: rating,
      ratingFraction: ratingFraction(rating, scale),
      ratingLabel: ratingLabel(rating, scale),
      ratingVariant: ratingVariant(rating, scale),
      iconSrc: 'assets/svg/star.svg',
      comment: review.comment || '',
      stayDates: review.stay_dates || '',
      subRatingsSummary: buildSubRatingsSummary(review.ratings),
      responseLabel: response.label,
      responseVariant: response.variant,
      responsePillClass: response.pillClass,
    };
  }

  // Switching views lazy-loads the expected list the first time it is opened,
  // then just re-narrows whichever view is now active by the current search.
  onViewChange(view: string): void {
    this.selectedView = view as ReviewsView;
    if (this.selectedView === 'expected' && !this.isExpectedLoaded && !this.showAboutThisPage) {
      this.loadExpectedReviews();
    }
    this.refreshActiveView();
    this.cdr.markForCheck();
  }

  private refreshActiveView(): void {
    if (this.selectedView === 'inbound') {
      this.refreshView();
    } else {
      this.refreshExpectedView();
    }
  }

  // Desktop table cell click → open the matching detail panel. The table emits
  // { column, element } where element is the row we built (carrying its id).
  onReviewCellClick(event: { column?: string; element?: { id?: number } }): void {
    this.openReviewDetail(event?.element?.id);
  }

  onExpectedCellClick(event: { column?: string; element?: { id?: number } }): void {
    this.openLeaveReview(event?.element?.id);
  }

  // Respond to an inbound review: opens the review-detail panel (respond form).
  openReviewDetail(reviewId: number | undefined): void {
    if (this.showAboutThisPage || reviewId === undefined) { return; }
    const review = this.reviewsById.get(reviewId);
    if (!review) { return; }

    this.storage.save('dialog-right-side-open', true);
    this.storage.save('dialog-params', { review });
    this.rightSidePanelService.setDialogPageTitle('Review');

    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.openRightSidePanel(ReviewDetailComponent);
    } else {
      this.navCtrl.navigateForward('revenue/review-detail');
    }
  }

  // Leave / queue a guest review for a checked-out reservation.
  openLeaveReview(reservationId: number | undefined): void {
    if (this.showAboutThisPage || reservationId === undefined) { return; }
    const expected = this.expectedById.get(reservationId);
    if (!expected) { return; }

    this.storage.save('dialog-right-side-open', true);
    this.storage.save('dialog-params', { expected });
    this.rightSidePanelService.setDialogPageTitle('Leave Guest Review');

    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.openRightSidePanel(LeaveGuestReviewComponent);
    } else {
      this.navCtrl.navigateForward('revenue/leave-guest-review');
    }
  }

  private async reloadInbound(): Promise<void> {
    try {
      this.applyReviews(await this.propertyReviewsService.getGuestReviews());
    } catch {
      // Non-fatal: keep the current list on a refresh failure.
    }
  }

  private async reloadExpected(): Promise<void> {
    this.isExpectedLoaded = false;
    await this.loadExpectedReviews();
  }

  async loadExpectedReviews(): Promise<void> {
    if (this.isExpectedLoading) {
      return;
    }
    this.isExpectedLoading = true;
    let expected: ExpectedReview[] = [];
    try {
      expected = await this.propertyReviewsService.getExpectedReviews();
    } catch {
      expected = [];
    }
    this.applyExpectedReviews(expected);
    this.isExpectedLoading = false;
  }

  private applyExpectedReviews(expected: ExpectedReview[]): void {
    this.expectedById = new Map(expected.map(item => [item.reservationId, item]));
    this.allExpectedCards = expected.map(item => this.toExpectedCard(item));
    this.refreshExpectedView();
    this.isExpectedLoaded = true;
    this.cdr.markForCheck();
  }

  private toExpectedCard(expected: ExpectedReview): ExpectedReviewCard {
    const reviewLeft = expected.status === 'review_left';
    return {
      id: expected.reservationId,
      listingName: expected.listingName || 'Unknown Property',
      guestName: expected.guestName || '',
      channel: this.formatPlatform(expected.platform),
      stayDates: this.formatStayRange(expected.checkInDate, expected.checkOutDate),
      checkOutSort: this.toSortableTime(expected.checkOutDate),
      statusLabel: reviewLeft ? 'Review left' : 'Needs review',
      statusVariant: reviewLeft ? 'impact-high' : 'impact-medium',
      statusPillClass: reviewLeft ? 'reviews-status-pill--review-left' : 'reviews-status-pill--needs-review',
    };
  }

  private refreshExpectedView(): void {
    const sorted = [...this.allExpectedCards].sort((a, b) => this.compareExpectedCards(a, b));
    this.expectedCards = this.searchText
      ? sorted.filter(card => this.matchesExpectedSearch(card, this.searchText))
      : sorted;
    this.expectedRows = this.buildExpectedRows(this.expectedCards);
  }

  private buildExpectedRows(cards: ExpectedReviewCard[]): any[] {
    return cards.map(card => ({
      id: card.id,
      'Property': { value: card.listingName },
      'Guest': { value: card.guestName || '—' },
      'Channel': { value: card.channel },
      'Stay': { value: card.stayDates || '—' },
      'Status': { value: this.pillHtml(card.statusLabel, card.statusVariant) },
      'last-column': { value: '' },
    }));
  }

  // The table renders cell values via innerHTML, so status/response badges ship
  // as markup (shared by the inbound Response and expected Status columns)
  // instead of the tidy-impact-pill component they stand in for.
  private pillHtml(label: string, variant: string): string {
    const palette = ReviewsTabComponent.RATING_PILL_COLORS[variant];
    return `<span style="display:inline-block;padding:2px 10px;border-radius:10px;font-size:12px;font-weight:500;` +
      `background:${palette.background};color:${palette.text} !important;">${label}</span>`;
  }

  private matchesExpectedSearch(card: ExpectedReviewCard, lower: string): boolean {
    return card.listingName.toLowerCase().includes(lower) ||
      card.guestName.toLowerCase().includes(lower) ||
      card.channel.toLowerCase().includes(lower) ||
      card.statusLabel.toLowerCase().includes(lower);
  }

  private compareExpectedCards(a: ExpectedReviewCard, b: ExpectedReviewCard): number {
    const dir = this.expectedSortAsc ? 1 : -1;
    if (this.expectedSortKey === 'stay') {
      return (a.checkOutSort - b.checkOutSort) * dir;
    }
    const key = this.expectedSortKeyField(this.expectedSortKey);
    return a[key].localeCompare(b[key]) * dir;
  }

  private expectedSortKeyField(key: ExpectedSortKey): 'listingName' | 'guestName' | 'channel' | 'statusLabel' {
    switch (key) {
      case 'property': return 'listingName';
      case 'guest': return 'guestName';
      case 'channel': return 'channel';
      default: return 'statusLabel';
    }
  }

  expectedSortChanged(sort: any): void {
    const key = ReviewsTabComponent.EXPECTED_SORT_KEYS[sort?.active];
    if (!key || !sort.direction) { return; }
    this.expectedSortKey = key;
    this.expectedSortAsc = sort.direction === 'asc';
    this.refreshExpectedView();
    this.cdr.markForCheck();
  }

  private formatStayRange(checkIn: string, checkOut: string): string {
    const start = checkIn ? LuxonDateTime.fromISO(checkIn) : null;
    const end = checkOut ? LuxonDateTime.fromISO(checkOut) : null;
    if (start?.isValid && end?.isValid) {
      return `${start.toFormat('MMM d')} - ${end.toFormat('MMM d, yyyy')}`;
    }
    if (end?.isValid) {
      return end.toFormat('MMM d, yyyy');
    }
    return checkOut || checkIn || '';
  }

  private toSortableTime(date: string): number {
    const time = date ? new Date(date).getTime() : NaN;
    return Number.isNaN(time) ? 0 : time;
  }

  get hasExpectedReviews(): boolean {
    return this.allExpectedCards.length > 0;
  }

  readonly expectedEmptyStateMessage = 'No reservations need a review yet. Checked-out stays will appear here so you know which guests still need a review.';

  // Sort applies to the whole set, then the search text narrows what renders,
  // so both the desktop table and the mobile card list stay in sync.
  private refreshView(): void {
    const sorted = [...this.allReviewCards].sort((a, b) => this.compareCards(a, b));
    this.reviewCards = this.searchText
      ? sorted.filter(card => this.matchesSearch(card, this.searchText))
      : sorted;
    this.rows = this.buildRows(this.reviewCards);
  }

  private buildRows(cards: ReviewCard[]): any[] {
    return cards.map(card => ({
      id: card.id,
      'Guest': { value: card.guestName, icon: ReviewsTabComponent.RATING_STAR_ICONS[card.ratingVariant] },
      'Property': { value: card.listingName },
      'Channel': { value: card.channel },
      'Rating': { value: this.ratingCellHtml(card) },
      'Review': { value: card.comment || '—', truncate: true },
      'Stay': { value: card.stayDates || '—' },
      'Response': { value: this.pillHtml(card.responseLabel, card.responseVariant) },
      'last-column': { value: '' },
    }));
  }

  // The table renders cell values via innerHTML, so the rating badge ships as
  // markup instead of the tidy-impact-pill component it replaces. The per-category
  // breakdown rides along as a hover tooltip since the column is too narrow to list it.
  private ratingCellHtml(card: ReviewCard): string {
    const palette = ReviewsTabComponent.RATING_PILL_COLORS[card.ratingVariant];
    const title = card.subRatingsSummary ? ` title="${card.subRatingsSummary}"` : '';
    return `<span${title} style="display:inline-block;padding:2px 10px;border-radius:10px;font-size:12px;font-weight:500;` +
      `cursor:${card.subRatingsSummary ? 'help' : 'default'};` +
      `background:${palette.background};color:${palette.text} !important;">${card.ratingLabel}</span>`;
  }

  private matchesSearch(card: ReviewCard, lower: string): boolean {
    return card.listingName.toLowerCase().includes(lower) ||
      card.guestName.toLowerCase().includes(lower) ||
      card.channel.toLowerCase().includes(lower) ||
      card.comment.toLowerCase().includes(lower) ||
      card.responseLabel.toLowerCase().includes(lower);
  }

  private compareCards(a: ReviewCard, b: ReviewCard): number {
    const dir = this.sortAsc ? 1 : -1;
    if (this.sortKey === 'rating') {
      return (a.ratingFraction - b.ratingFraction) * dir;
    }
    const key = this.sortKeyField(this.sortKey);
    return a[key].localeCompare(b[key]) * dir;
  }

  private sortKeyField(key: ReviewSortKey): 'guestName' | 'listingName' | 'channel' | 'comment' | 'stayDates' | 'responseLabel' {
    switch (key) {
      case 'guest': return 'guestName';
      case 'property': return 'listingName';
      case 'channel': return 'channel';
      case 'review': return 'comment';
      case 'response': return 'responseLabel';
      default: return 'stayDates';
    }
  }

  sortChanged(sort: any): void {
    const key = ReviewsTabComponent.SORT_KEYS[sort?.active];
    if (!key || !sort.direction) { return; }
    this.sortKey = key;
    this.sortAsc = sort.direction === 'asc';
    this.refreshView();
    this.cdr.markForCheck();
  }

  private formatPlatform(platform: string): string {
    return ReviewsTabComponent.PLATFORM_LABELS[platform] ?? (platform || '');
  }

  readonly emptyStateMessage = 'No reviews yet. Reviews will appear here automatically when guests leave feedback. The review agent triggers on new reviews to help you respond promptly.';

  get hasReviews(): boolean {
    return this.allReviewCards.length > 0;
  }

  private loadMockData() {
    const mockProperties = [
      'Oceanfront Condo', 'Mountain Retreat', 'Downtown Loft',
      'Lakeside Cabin', 'Sunset Villa', 'Cozy Cottage'
    ];
    const mockGuests = [
      'Sarah M.', 'James K.', 'Emily R.', 'Michael T.', 'Lisa W.',
      'David H.', 'Rachel P.', 'Chris B.', 'Amanda L.', 'Kevin S.',
      'Jessica N.', 'Brian F.', 'Olivia G.', 'Daniel C.', 'Megan A.'
    ];
    const mockPlatforms = ['airbnb', 'vrbo', 'booking_com'];
    const mockRatings = [5, 4, 5, 3, 4, 5, 4];
    const mockComments = [
      'The place was spotless and exactly as pictured. Would happily stay again!',
      'Wonderful experience from check-in to check-out. Highly recommend.',
      'Great location, though the place could have been a little cleaner.',
      'Perfect getaway for the weekend. Everything we needed was there.',
      'Highly recommend — the host was responsive and the space was beautiful.',
      'Amazing views and a very comfortable bed. Loved every minute.',
      'Very comfortable stay, close to everything we wanted to see.',
      'Nice location and easy self check-in. Would book again.',
      'Exceeded our expectations. The kitchen was fully stocked.',
      'Will return! Such a relaxing spot with great amenities.',
      'Loved it here. The photos do not do it justice.',
      'Decent place for the price, a few small things to fix.',
      'Fantastic host who went above and beyond for us.',
      'Beautiful property in a quiet neighborhood. Very clean.',
      'Good value and a smooth booking experience overall.'
    ];
    const mockStayDates = [
      'Mar 24 - Mar 28, 2026', 'Mar 21 - Mar 25, 2026', 'Mar 18 - Mar 22, 2026',
      'Mar 15 - Mar 19, 2026', 'Mar 11 - Mar 15, 2026', 'Mar 8 - Mar 12, 2026',
      'Mar 4 - Mar 8, 2026', 'Mar 1 - Mar 5, 2026', 'Feb 25 - Mar 1, 2026',
      'Feb 22 - Feb 26, 2026', 'Feb 18 - Feb 22, 2026', 'Feb 14 - Feb 18, 2026',
      'Feb 10 - Feb 14, 2026', 'Feb 6 - Feb 10, 2026', 'Feb 2 - Feb 6, 2026'
    ];
    const mockReviews: GuestReview[] = [];
    for (let i = 0; i < 15; i++) {
      const responded = i % 3 === 0;
      const platform = mockPlatforms[i % mockPlatforms.length];
      // Airbnb scores out of 5; VRBO / Booking.com out of 10 — scale the demo
      // rating so the pill shows "5/5" vs "10/10" like the real platforms.
      const scale = platform === 'airbnb' ? 5 : 10;
      const baseRating = mockRatings[i % mockRatings.length];
      mockReviews.push({
        id: i + 1,
        platform,
        guest_name: mockGuests[i],
        overall_rating: scale === 10 ? baseRating * 2 : baseRating,
        rating_scale: scale,
        comment: mockComments[i],
        stay_dates: mockStayDates[i],
        listing_name: mockProperties[i % mockProperties.length],
        address_listing_id: i + 1,
        ratings: {
          cleanliness: 5,
          accuracy: 5,
          checkIn: 5,
          communication: mockRatings[i % mockRatings.length],
          location: 4,
          value: mockRatings[(i + 1) % mockRatings.length],
        },
        reviewed_at: '',
        responded_at: responded ? new Date().toISOString() : null,
        response_status: responded ? 'responded' : 'needs_response',
        host_response: responded ? 'Thank you so much for staying with us!' : null,
        created_at: '',
      });
    }
    this.applyReviews(mockReviews);

    const mockExpected: ExpectedReview[] = [];
    for (let i = 0; i < 8; i++) {
      const checkIn = LuxonDateTime.local().minus({ days: (i + 1) * 5 + 4 });
      const checkOut = checkIn.plus({ days: 4 });
      mockExpected.push({
        reservationId: 1000 + i,
        listingName: mockProperties[i % mockProperties.length],
        guestName: mockGuests[i],
        platform: 'airbnb',
        checkInDate: checkIn.toISODate() ?? '',
        checkOutDate: checkOut.toISODate() ?? '',
        status: 'needs_review',
      });
    }
    this.applyExpectedReviews(mockExpected);
  }

  private setupOverlayObserver() {
    const updateTop = () => {
      if (!this._reviewsHeader) {return;}
      const top = this._reviewsHeader.nativeElement.getBoundingClientRect().top - 8;
      this.overlayTop = `${top  }px`;
      this.cdr.markForCheck();
    };
    this.overlayResizeObserver = new ResizeObserver(() => updateTop());
    this.overlayResizeObserver.observe(document.body);
    setTimeout(() => updateTop());
  }

  ngOnDestroy() {
    this.overlayResizeObserver?.disconnect();
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.overlayResizeObserver?.disconnect();
    this.aboutThisPageService.dismissAboutThisPage('reviews');
    this.allReviewCards = [];
    this.reviewCards = [];
    this.rows = [];
    this.allExpectedCards = [];
    this.expectedCards = [];
    this.expectedRows = [];
    this.isExpectedLoaded = false;
    this.cdr.markForCheck();
  }
}
